import { useState, useEffect } from 'react';
import { C, FONTS } from '../../tokens/design';
import Shell from '../../components/layout/Shell';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Tag from '../../components/ui/Tag';
import Skeleton from '../../components/ui/Skeleton';
import PageError from '../../components/ui/PageError';
import { useUserProfile } from '../../hooks/useAccountData';
import { useApiClient } from '../../api/apiClient';
import { useAuth } from '../../context/AuthContext';

function Field({ label, children, hint }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: 9, color: C.t3, marginTop: 4, fontFamily: FONTS.mono }}>{hint}</div>}
    </div>
  );
}

function Input({ value, onChange, placeholder, disabled }) {
  return <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} style={{ width: '100%', background: disabled ? C.s1 : C.s2, border: `1px solid ${C.b1}`, borderRadius: 6, padding: '9px 11px', color: disabled ? C.t2 : C.t1, fontSize: 12, fontFamily: FONTS.mono, outline: 'none', opacity: disabled ? 0.7 : 1 }} />;
}

function getInitials(name) {
  return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
}

export default function Profile() {
  const { data: profile, loading, error, refetch } = useUserProfile();
  const api = useApiClient();
  const { user } = useAuth();
  const [form, setForm] = useState({
    displayName: '', email: '', phone: '', title: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);

  useEffect(() => {
    if (profile) {
      setForm({
        displayName: profile.displayName || profile.name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        title: profile.title || '',
      });
    }
  }, [profile]);

  const u = (k, v) => { setForm(f => ({ ...f, [k]: v })); setSaveResult(null); };

  const handleSave = async () => {
    setSaving(true);
    setSaveResult(null);
    try {
      await api.put('/me/profile', {
        DisplayName: form.displayName,
        Title: form.title,
        Phone: form.phone,
      });
      setSaveResult({ ok: true, msg: 'Profile updated successfully.' });
      setTimeout(() => setSaveResult(prev => prev?.ok ? null : prev), 3000);
      if (refetch) refetch();
    } catch (err) {
      setSaveResult({ ok: false, msg: `Failed to save: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const displayName = form.displayName || user?.name || 'User';
  const initials = getInitials(displayName);
  const roleName = profile ? ({ 0: 'MSP Admin', 1: 'Security Analyst', 2: 'Account Manager', 3: 'Read-Only' }[profile.role] || 'User') : 'User';

  if (loading) {
    return (
      <Shell title="My Profile" sub="Account details & preferences">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 14 }}>
          <Card style={{ padding: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
              <Skeleton width={64} height={64} style={{ borderRadius: '50%' }} />
              <div style={{ flex: 1 }}>
                <Skeleton width="50%" height={18} style={{ marginBottom: 8 }} />
                <Skeleton width="30%" height={11} style={{ marginBottom: 6 }} />
                <Skeleton width={80} height={18} />
              </div>
            </div>
            <Skeleton width="40%" height={13} style={{ marginBottom: 14 }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {[0,1,2,3].map(i => <div key={i}><Skeleton width="30%" height={10} style={{ marginBottom: 5 }} /><Skeleton height={34} /></div>)}
            </div>
          </Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Card style={{ padding: 18 }}><Skeleton width="60%" height={12} style={{ marginBottom: 12 }} />{[0,1,2].map(i => <Skeleton key={i} height={18} style={{ marginBottom: 8 }} />)}</Card>
            <Card style={{ padding: 18 }}><Skeleton width="60%" height={12} style={{ marginBottom: 12 }} />{[0,1,2,3].map(i => <Skeleton key={i} height={14} style={{ marginBottom: 6 }} />)}</Card>
          </div>
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell title="My Profile" sub="Account details & preferences">
        <PageError error={error} onRetry={refetch} />
      </Shell>
    );
  }

  return (
    <Shell title="My Profile" sub="Account details & preferences">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Card style={{ padding: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg,#334155,#1e293b)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{initials}</div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: C.t1, fontFamily: FONTS.sans }}>{displayName}</div>
                <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginTop: 2 }}>{form.title || form.email}</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <Tag label={roleName} color={C.sky} />
                </div>
              </div>
            </div>

            <>
                <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 14, color: C.t1 }}>Personal Information</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <Field label="Display Name"><Input value={form.displayName} onChange={v => u('displayName', v)} /></Field>
                  <Field label="Email Address" hint="Email is managed through your identity provider."><Input value={form.email} disabled /></Field>
                  <Field label="Job Title"><Input value={form.title} onChange={v => u('title', v)} /></Field>
                  <Field label="Phone"><Input value={form.phone} onChange={v => u('phone', v)} /></Field>
                </div>
                {saveResult && (
                  <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono, background: saveResult.ok ? `${C.grn}12` : C.redBg, border: `1px solid ${saveResult.ok ? C.grn : C.red}33`, color: saveResult.ok ? C.grn : C.red }}>
                    {saveResult.msg}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                  <Button variant="pri" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
                </div>
              </>
          </Card>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Card style={{ padding: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 12, color: C.t1 }}>Account Details</div>
            {[
              ['Role', roleName],
              ['Email', form.email || user?.email || '—'],
              ['Organization', profile?.mspName || '—'],
            ].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${C.b2}` }}>
                <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{l}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: C.t1, fontFamily: FONTS.mono }}>{v}</span>
              </div>
            ))}
          </Card>

          <Card style={{ padding: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 12, color: C.t1 }}>Permissions</div>
            {['View all clients', 'Manage findings', 'Run scans', 'Generate reports', 'Manage team members', 'Manage API keys', 'Access audit log', 'Modify settings'].map(p => (
              <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 0' }}>
                <span style={{ fontSize: 10, color: C.grn }}>●</span>
                <span style={{ fontSize: 11, color: C.t1, fontFamily: FONTS.mono }}>{p}</span>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </Shell>
  );
}
